import {
  getKioskAudioSlot,
  isKioskAudioSlotKey,
  type KioskAudioLanguage,
  type KioskAudioSlotKey,
} from "@common/kiosk-audio/slots";

/** A clip cash-agent has on disk, from `GET /kiosk-audio/manifest`. */
export type KioskAudioClip = {
  slot: string;
  lang: string;
  version: string;
};

/** The one slot whose clip plays on every load, not just once per session. */
const ALWAYS_PLAYED_SLOT: KioskAudioSlotKey = "page.start-screen";
const PLAYED_STORAGE_KEY = "kiosk-audio-played";

/** One mounted slot declaration. `handled` once this mount played (or was due to). */
type MountedSlot = { slot: KioskAudioSlotKey; handled: boolean };

type AudioLike = Pick<HTMLAudioElement, "play" | "pause" | "src"> & {
  onended: (() => void) | null;
};

export type KioskAudioControllerOptions = {
  /** Builds the clip URL served by cash-agent. */
  clipUrl: (clip: KioskAudioClip) => string;
  /** Session-scoped storage for the played set (sessionStorage in the app). */
  storage?: Pick<Storage, "getItem" | "setItem">;
  createAudio?: () => AudioLike;
};

/**
 * Plays the kiosk audio clips through one shared Audio element. Components
 * declare the slot they show with `mountSlot`; the controller decides what plays:
 *
 * - a slot plays the first time it is shown in a session (the played set lives
 *   in sessionStorage, which the start screen clears); `page.start-screen`
 *   plays on every load;
 * - unmounting a slot (navigation, step/status change, closing a modal) stops
 *   its clip at once; nothing is replayed, so closing a modal never brings the
 *   page clip back;
 * - a modal interrupts the page clip only when it has a clip of its own, and a
 *   page appearing under a talking modal stays silent;
 * - switching language replays the current slot in the new language, with no
 *   fallback to another language;
 * - muting stops playback, unmuting replays nothing;
 * - disabled (feature flag off) plays nothing at all.
 */
export function createKioskAudioController(options: KioskAudioControllerOptions) {
  const storage = options.storage ?? sessionStorage;
  const createAudio = options.createAudio ?? (() => new Audio() as AudioLike);

  const state: {
    audio: AudioLike | null;
    enabled: boolean;
    muted: boolean;
    language: KioskAudioLanguage;
    clips: Map<string, KioskAudioClip>;
    playing: MountedSlot | null;
    /** Mounted slot declarations, oldest first. */
    mounted: MountedSlot[];
  } = {
    audio: null,
    enabled: false,
    muted: false,
    language: "hu",
    clips: new Map(),
    playing: null,
    mounted: [],
  };

  const clipKey = (slot: string, lang: string) => `${slot}\u0000${lang}`;
  const clipFor = (slot: KioskAudioSlotKey) => state.clips.get(clipKey(slot, state.language));
  const isModal = (entry: MountedSlot) => getKioskAudioSlot(entry.slot).kind === "modal";

  /** The slot on screen: the newest modal, else the newest page. */
  const current = () => [...state.mounted].reverse().find(isModal) ?? state.mounted.at(-1);

  const readPlayed = (): string[] => {
    try {
      const parsed = JSON.parse(storage.getItem(PLAYED_STORAGE_KEY) ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const markPlayed = (slot: KioskAudioSlotKey) => {
    const played = readPlayed();
    if (!played.includes(slot)) {
      storage.setItem(PLAYED_STORAGE_KEY, JSON.stringify([...played, slot]));
    }
  };

  const wasPlayed = (slot: KioskAudioSlotKey) =>
    slot !== ALWAYS_PLAYED_SLOT && readPlayed().includes(slot);

  const stop = () => {
    state.playing = null;
    if (state.audio) {
      state.audio.onended = null;
      state.audio.pause();
      state.audio.src = "";
    }
  };

  const start = (entry: MountedSlot, clip: KioskAudioClip) => {
    stop();
    state.audio ??= createAudio();
    state.playing = entry;
    const clearIfCurrent = () => {
      if (state.playing === entry) {
        state.playing = null;
      }
    };
    state.audio.onended = clearIfCurrent;
    state.audio.src = options.clipUrl(clip);
    // Autoplay can still be refused (e.g. a dev browser without the kiosk's
    // Chrome flag); a silent screen is the right outcome then.
    state.audio.play().catch(clearIfCurrent);
  };

  /** A declared slot is on screen: play it unless the rules say otherwise. */
  const show = (entry: MountedSlot) => {
    if (!state.enabled || entry.handled) {
      return;
    }
    if (!isModal(entry) && state.playing && isModal(state.playing)) {
      return;
    }
    if (wasPlayed(entry.slot)) {
      entry.handled = true;
      return;
    }
    const clip = clipFor(entry.slot);
    if (!clip) {
      // No clip (yet): a modal without one leaves the page clip playing. The
      // mount stays unhandled so a manifest arriving later can still play it.
      return;
    }
    entry.handled = true;
    markPlayed(entry.slot);
    if (!state.muted) {
      start(entry, clip);
    }
  };

  return {
    /** Declare that `slot` is on screen. Returns the matching unmount. */
    mountSlot(slot: KioskAudioSlotKey): () => void {
      const entry: MountedSlot = { slot, handled: false };
      state.mounted = [...state.mounted, entry];
      show(entry);

      return () => {
        state.mounted = state.mounted.filter((other) => other !== entry);
        if (state.playing === entry) {
          stop();
        }
      };
    },

    setClips(next: KioskAudioClip[]) {
      state.clips = new Map(
        next
          .filter((clip) => isKioskAudioSlotKey(clip.slot))
          .map((clip) => [clipKey(clip.slot, clip.lang), clip]),
      );
      // A slot shown before its clip was known (fresh page load) plays once
      // the manifest arrives — only the one on screen.
      const entry = current();
      if (entry && !state.playing) {
        show(entry);
      }
    },

    setEnabled(next: boolean) {
      if (next === state.enabled) {
        return;
      }
      state.enabled = next;
      const entry = current();
      if (!state.enabled) {
        stop();
      } else if (entry) {
        // Flags often arrive after the first screen mounted.
        show(entry);
      }
    },

    setMuted(next: boolean) {
      state.muted = next;
      if (state.muted) {
        stop();
      }
    },

    setLanguage(next: KioskAudioLanguage) {
      if (next === state.language) {
        return;
      }
      state.language = next;
      stop();
      const entry = current();
      if (!entry || !state.enabled || state.muted) {
        return;
      }
      // No fallback language: without a clip in the new language it stays silent.
      const clip = clipFor(entry.slot);
      if (clip) {
        entry.handled = true;
        markPlayed(entry.slot);
        start(entry, clip);
      }
    },

    /** Slot whose clip is playing right now (for tests and debugging). */
    getPlayingSlot(): KioskAudioSlotKey | null {
      return state.playing?.slot ?? null;
    },
  };
}

export type KioskAudioController = ReturnType<typeof createKioskAudioController>;
